import React from 'react'
import { Platform, StyleSheet } from 'react-native'
import { Box, IBoxProps } from 'native-base'

import { buildComponent } from 'src/components/factory'
import * as UI from 'native-base'

const SIDES = ['Top', 'Bottom', 'Left', 'Right']
const CORNERS = ['TopLeft', 'TopRight', 'BottomLeft', 'BottomRight']
const radius = (corners: string[]) => corners.map((c) => `border${c}Radius`)

// Longhand styles behind the shorthands used by the default styles below
const SHORTHANDS: Record<string, string[]> = {
  borderRadius: radius(CORNERS),
}
// Longhand styles set by each native-base padding, margin or radius prop
const PROPS: Record<string, string[]> = {}
const define = (names: string[], longhands: string[]) =>
  names.forEach((name) => (PROPS[name] = longhands))

for (const [kind, short] of [
  ['padding', 'p'],
  ['margin', 'm'],
]) {
  const sides = (list: string[]) => list.map((side) => kind + side)
  SHORTHANDS[kind] = sides(SIDES)
  SHORTHANDS[`${kind}Vertical`] = sides(['Top', 'Bottom'])
  SHORTHANDS[`${kind}Horizontal`] = sides(['Left', 'Right'])
  define([short, kind], sides(SIDES))
  define([`${short}y`, `${kind}Y`, `${kind}Vertical`], sides(['Top', 'Bottom']))
  define([`${short}x`, `${kind}X`, `${kind}Horizontal`], sides([
    'Left',
    'Right',
  ]))
  SIDES.forEach((side) =>
    define([short + side[0].toLowerCase(), kind + side], [kind + side]),
  )
}
define(['rounded', 'borderRadius'], radius(CORNERS))
define(['roundedTop', 'borderTopRadius'], radius(['TopLeft', 'TopRight']))
define(['roundedBottom', 'borderBottomRadius'], radius([
  'BottomLeft',
  'BottomRight',
]))
define(['roundedLeft', 'borderLeftRadius'], radius(['TopLeft', 'BottomLeft']))
define(['roundedRight', 'borderRightRadius'], radius([
  'TopRight',
  'BottomRight',
]))
CORNERS.forEach((corner) =>
  define([`rounded${corner}`, `border${corner}Radius`], radius([corner])),
)

// The padding, margin and radius props given to a Card must win over its
// default styles. React Native gives priority to longhand styles, and
// react-native-web 0.17 applied the props last, but react-native-web 0.21
// keeps the default shorthands (paddingHorizontal over px={0}...). On the web,
// the defaults are therefore expanded to longhands and those set by a prop
// are dropped.
function withoutOverriddenDefaults(defaults: any, props: Record<string, any>) {
  if (Platform.OS !== 'web') {
    return defaults
  }

  const style: Record<string, unknown> = {}
  Object.entries(StyleSheet.flatten(defaults) ?? {}).forEach(([key, value]) => {
    ;(SHORTHANDS[key] ?? [key]).forEach((longhand) => (style[longhand] = value))
  })
  Object.keys(props).forEach((prop) => {
    if (props[prop] !== undefined) {
      PROPS[prop]?.forEach((longhand) => delete style[longhand])
    }
  })

  return style
}

export const Card = buildComponent<
  IBoxProps & {
    containerStyle?: IBoxProps['style']
    flat?: boolean
    opaque?: boolean
  }
>()
  .withStyles({
    container: {
      main: {
        position: 'relative',
        padding: 0,
        marginVertical: 8,
        marginHorizontal: 4,
      },
    },
    shadow: {
      main: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: -4,
        borderRadius: 16,
        backgroundColor: '#FFFFFFBB',
      },
      custom: {
        flat: {
          bottom: 0,
        },
      },
    },
    card: {
      main: {
        paddingVertical: 8,
        paddingHorizontal: 16,
        borderRadius: 16,
        backgroundColor: '#FFFFFFBB',
        flex: 1,
      },
      custom: {
        opaque: {
          backgroundColor: '#FFFFFF',
        },
      },
    },
  })
  .withLifecycle(({ props }) => {
    const innerProps: UI.IBoxProps = { ...props }
    const boxProps: Partial<UI.IBoxProps> = {}
    const shadowProps: Partial<UI.IBoxProps> = {}
    Object.keys(innerProps).forEach((prop) => {
      if (prop.startsWith('margin') || prop.startsWith('flex')) {
        boxProps[prop as keyof UI.IBoxProps] =
          innerProps[prop as keyof UI.IBoxProps]
        delete innerProps[prop as keyof UI.IBoxProps]
      }

      if (prop.startsWith('border')) {
        shadowProps[prop as keyof UI.IBoxProps] =
          innerProps[prop as keyof UI.IBoxProps]
      }
    })

    return {
      innerProps,
      boxProps,
      shadowProps,
    }
  })
  .withRender(({ props, styles, lifecycle }) => {
    return (
      <Box
        {...lifecycle.boxProps}
        flexDirection="row"
        style={[
          withoutOverriddenDefaults(styles.container(), lifecycle.boxProps),
          props.containerStyle,
        ]}
      >
        <Box
          {...lifecycle.shadowProps}
          style={withoutOverriddenDefaults(
            styles.shadow({ flat: props.flat }),
            lifecycle.shadowProps,
          )}
        />
        <Box
          {...lifecycle.innerProps}
          style={[
            withoutOverriddenDefaults(
              styles.card({ opaque: props.opaque }),
              lifecycle.innerProps,
            ),
            lifecycle.innerProps.style,
          ]}
        >
          {props.children}
        </Box>
      </Box>
    )
  })
